import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { notificar } from '../utils/notify';
import ContratoAnexo from '../components/ContratoAnexo';
import VinculoBadge from '../components/VinculoBadge';
import { vinculoCurto } from '../utils/vinculo';
import '../styles/bporh_admissoes.css';

// Página de Prorrogação de Colaborador — contratos de experiência (espelho Convenia).
// Todo CLT novo tem um contrato de experiência (30+60, 45+45...); ao fim de cada
// período o gestor da área precisa confirmar se mantém o colaborador ou encaminha
// o desligamento. A tela lista os contratos por empresa, mostra os marcos de cada
// colaborador e envia o e-mail de confirmação ao gestor (registrando o envio).
// Mesmo layout das Admissões/Rescisões/Férias: cards adm-*, painel-resumo e filtros.

const JANELAS = [
  { dias: 30, rotulo: 'Últimos 30 dias + em experiência' },
  { dias: 90, rotulo: 'Últimos 90 dias + em experiência' },
  { dias: 36500, rotulo: 'Todo o período' },
];

const nomeCompleto = (c) =>
  [c.nome, c.sobrenome].filter(Boolean).join(' ') || c.nome_social || '';   // nome completo; social só de reserva

// badge de estado de um marco (fim de período): a cor conta a urgência,
// o aviso enviado ganha de tudo (já está com o gestor)
// Ordem de trabalho da tela: quem precisa de e-mail primeiro. Vencido vem na
// frente, depois o que está na janela de aviso, depois o tranquilo e por último
// quem já foi avisado. Dentro do grupo manda o ATRASO — dias_para_fim mais
// negativo (mais atrasado) primeiro, e entre os que ainda não venceram, o que
// vence antes.
const pesoUrgencia = (c) => {
  const p = c.proximo_marco || {};
  if (p.historico) return 4;          // regularizado: fim da fila
  if (p.resolvido) return 3;          // aviso + termo em ordem
  if (p.vencido) return 0;
  if (p.alerta) return 1;
  return 2;
};

const porUrgencia = (a, z) => (pesoUrgencia(a) - pesoUrgencia(z))
  || ((a.proximo_marco?.dias_para_fim ?? 9999) - (z.proximo_marco?.dias_para_fim ?? 9999));

const BadgeMarco = ({ m }) => {
  // admitido antes do início do controle (ago/2026): histórico regularizado,
  // nada a cobrar — nem e-mail, nem termo
  if (m.historico) {
    return (
      <span className="badge bg-light text-muted border"
        title="Admissão anterior ao início do controle — regularizado, sem cobrança de aviso ou contrato">
        <i className="fas fa-box-archive me-1"></i>histórico
      </span>
    );
  }
  // aviso saiu mas o termo do 1º período ainda não foi anexado: não está
  // resolvido — a partir do corte os dois são obrigatórios
  if (m.aviso_enviado_em && m.pendente_contrato) {
    return (
      <span className="badge" title={`Gestor avisado em ${m.aviso_enviado_em} — falta anexar o termo de prorrogação assinado`}
        style={{ backgroundColor: '#fff5da', color: '#8a6d00', border: '1.5px solid #f1be48' }}>
        <i className="fas fa-envelope-circle-check me-1"></i>avisado · falta o contrato
      </span>
    );
  }
  if (m.aviso_enviado_em) {
    return (
      <span className="badge" title={`Gestor avisado em ${m.aviso_enviado_em}${m.aviso_destinatarios ? ` — ${m.aviso_destinatarios}` : ''}`}
        style={{ backgroundColor: '#e6f7ef', color: '#116b47', border: '1.5px solid #1fa971' }}>
        <i className="fas fa-envelope-circle-check me-1"></i>avisado {m.aviso_enviado_em.slice(0, 10)}
      </span>
    );
  }
  if (m.vencido) {
    return (
      <span className="badge" title="Período já venceu e o gestor não foi avisado por aqui"
        style={{ backgroundColor: '#fdeaec', color: '#a11d2b', border: '1.5px solid #dc3545' }}>
        <i className="fas fa-triangle-exclamation me-1"></i>venceu há {-m.dias_para_fim}d
      </span>
    );
  }
  if (m.alerta) {
    return (
      <span className="badge" title="Dentro da janela de aviso — hora de perguntar ao gestor"
        style={{ backgroundColor: '#fff5da', color: '#8a6d00', border: '1.5px solid #f1be48' }}>
        <i className="fas fa-hourglass-half me-1"></i>vence em {m.dias_para_fim}d
      </span>
    );
  }
  return (
    <span className="badge bg-light text-muted border">
      em {m.dias_para_fim}d
    </span>
  );
};

const BpoRhProrrogacao = () => {
  const API = process.env.REACT_APP_API_BASE;
  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });

  const [mensagem, setMensagem] = useState('');
  useEffect(() => { notificar(mensagem); }, [mensagem]);

  const [empresas, setEmpresas] = useState([]);
  const PAINEL_VAZIO = { total: 0, a_vencer: 0, vencidos: 0, sem_gestor: 0, sem_tipo: 0 };
  const [painel, setPainel] = useState(PAINEL_VAZIO);
  const [carregando, setCarregando] = useState(true);
  const [dias, setDias] = useState(30);
  // só admissões deste mês em diante — pedido do time: os processos antigos
  // (antes de ago/2026) não serão mais tratados por aqui. O mesmo valor está
  // em PRORROGACAO_ADM_DE_PADRAO no backend, que é o que o Painel do Operador
  // usa: mudar um sem o outro faz as duas telas divergirem.
  const [admDe, setAdmDe] = useState('2026-08');

  const [searchParams, setSearchParams] = useSearchParams();
  const [empresaSelecionada, setEmpresaSelecionada] = useState(null);
  const [busca, setBusca] = useState('');
  const [filtroSituacao, setFiltroSituacao] = useState('');   // '', 'a_vencer', 'vencidos'
  // período de experiência é da CLT; outro vínculo aqui é cadastro a conferir,
  // então a tela abre filtrada em CLT mas deixa ver os demais
  const [filtroVinculo, setFiltroVinculo] = useState('CLT');

  const carregar = useCallback(() => {
    setCarregando(true);
    axios.get(`${API}/api/bporh/prorrogacoes/`,
      // campo limpo = 'todos' (sem o parâmetro o backend aplica o padrão)
      { params: { dias, adm_de: admDe || 'todos' }, headers: headers() })
      .then((res) => {
        setEmpresas(res.data.empresas || []);
        setPainel(res.data.painel || PAINEL_VAZIO);
      })
      .catch((error) => {
        setMensagem(`${error.response?.data?.erro || 'Não foi possível carregar as prorrogações'}`);
      })
      .finally(() => setCarregando(false));
  }, [dias, admDe]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, [carregar]);

  // deep-link ?cliente= (mantém a empresa aberta ao recarregar)
  useEffect(() => {
    const alvo = searchParams.get('cliente');
    if (alvo && empresas.length > 0 && !empresaSelecionada) {
      const emp = empresas.find(e => String(e.cliente_id) === alvo);
      if (emp) setEmpresaSelecionada(emp);
    }
  }, [empresas]); // eslint-disable-line react-hooks/exhaustive-deps

  const abrirEmpresa = (emp) => {
    setEmpresaSelecionada(emp);
    setBusca(''); setFiltroSituacao('');
    setSearchParams({ cliente: String(emp.cliente_id) });
  };
  const voltarEmpresas = () => {
    setEmpresaSelecionada(null);
    setSearchParams({});
  };

  // empresa selecionada sempre com os dados mais frescos do estado
  const empresaAtual = useMemo(() => {
    if (!empresaSelecionada) return null;
    return empresas.find(e => e.cliente_id === empresaSelecionada.cliente_id) || empresaSelecionada;
  }, [empresas, empresaSelecionada]);

  const colaboradoresFiltrados = useMemo(() => {
    if (!empresaAtual) return [];
    const b = busca.trim().toLowerCase();
    return (empresaAtual.colaboradores || []).filter(c => {
      if (b && ![nomeCompleto(c), c.cargo, c.departamento, c.gestor, c.tipo]
        .filter(Boolean).join(' ').toLowerCase().includes(b)) return false;
      const p = c.proximo_marco || {};
      if (filtroSituacao === 'a_vencer') return p.alerta && !p.resolvido;
      if (filtroSituacao === 'vencidos') return p.vencido && !p.resolvido;
      if (filtroSituacao === 'sem_tipo') return !c.tipo;
      return true;
    }).sort(porUrgencia);
  }, [empresaAtual, busca, filtroSituacao]);

  // ─── resumo geral (página principal): todos os colaboradores de todas as
  //     empresas numa tabela só, sem precisar entrar em cada card. Mais
  //     urgente primeiro: vencido > a vencer > tranquilo > já avisado. ───
  const geraisFiltrados = useMemo(() => {
    if (empresaAtual) return [];
    const b = busca.trim().toLowerCase();
    const lista = empresas.flatMap(emp =>
      (emp.colaboradores || []).map(c => ({
        ...c,
        cliente: c.cliente || emp.cliente,
        cliente_id: c.cliente_id ?? emp.cliente_id,
        _empresa: emp.cliente, _emp: emp,
      })));
    return lista
      .filter(c => {
        if (b && ![nomeCompleto(c), c._empresa, c.cargo, c.departamento, c.gestor, c.tipo]
          .filter(Boolean).join(' ').toLowerCase().includes(b)) return false;
        if (filtroVinculo === 'CLT' && vinculoCurto(c.modelo_contratacao) !== 'CLT') return false;
        if (filtroVinculo === 'outros' && vinculoCurto(c.modelo_contratacao) === 'CLT') return false;
        const p = c.proximo_marco || {};
        if (filtroSituacao === 'a_vencer') return p.alerta && !p.resolvido;
        if (filtroSituacao === 'vencidos') return p.vencido && !p.resolvido;
        if (filtroSituacao === 'sem_tipo') return !c.tipo;
        return true;
      })
      .sort(porUrgencia);
  }, [empresas, empresaAtual, busca, filtroSituacao, filtroVinculo]);

  // quantos têm experiência cadastrada sem ser CLT — indicador de cadastro errado
  const naoCLT = useMemo(() => empresas
    .flatMap(e => e.colaboradores || [])
    .filter(c => vinculoCurto(c.modelo_contratacao) !== 'CLT').length, [empresas]);

  // anexo do contrato de renovação (1º limite) — modal próprio, para não
  // depender de abrir o e-mail ao gestor
  const [anexoContrato, setAnexoContrato] = useState(null);   // {item}

  // ─── aviso ao gestor por e-mail (prévia + envio, padrão das Férias) ───
  const [aviso, setAviso] = useState(null);   // {item, marco, dados?, carregando}
  const [avisoDest, setAvisoDest] = useState('');
  const [avisoOperador, setAvisoOperador] = useState('');
  const [enviandoAviso, setEnviandoAviso] = useState(false);
  const [atualizandoPrevia, setAtualizandoPrevia] = useState(false);

  // trocar quem assina REFAZ a prévia no servidor (?operador_id=): a assinatura
  // do corpo tem de ser a de quem realmente envia, não a do operador sugerido.
  // Os destinatários editados na tela são preservados.
  const trocarOperadorAviso = (id) => {
    setAvisoOperador(id);
    if (!aviso?.item || !id) return;
    setAtualizandoPrevia(true);
    axios.get(`${API}/api/bporh/prorrogacoes/aviso/previa/`, {
      params: { cliente_id: aviso.item.cliente_id, convenia_id: aviso.item.convenia_id,
                marco: aviso.marco.fim, operador_id: id },
      headers: headers(),
    })
      .then((res) => setAviso(a => a && ({ ...a, dados: res.data })))
      .catch((error) => setMensagem(`${error.response?.data?.erro || 'Não foi possível atualizar a prévia'}`))
      .finally(() => setAtualizandoPrevia(false));
  };

  const abrirAviso = (item, marco) => {
    setAviso({ item, marco, carregando: true });
    axios.get(`${API}/api/bporh/prorrogacoes/aviso/previa/`, {
      params: { cliente_id: item.cliente_id, convenia_id: item.convenia_id, marco: marco.fim },
      headers: headers(),
    })
      .then((res) => {
        setAviso({ item, marco, dados: res.data, carregando: false });
        setAvisoDest(res.data.destinatarios || '');
        setAvisoOperador(res.data.operador_sugerido_id || '');
      })
      .catch((error) => {
        setMensagem(`${error.response?.data?.erro || 'Não foi possível montar a prévia'}`);
        setAviso(null);
      });
  };

  const enviarAviso = () => {
    const { item, marco, dados } = aviso;
    if (!avisoOperador) { setMensagem('Escolha o operador que assina o envio'); return; }
    if (!avisoDest.trim()) { setMensagem('Informe ao menos um destinatário'); return; }
    setEnviandoAviso(true);
    axios.post(`${API}/api/bporh/prorrogacoes/aviso/enviar/`, {
      cliente_id: item.cliente_id,
      convenia_id: item.convenia_id,
      marco: marco.fim,
      marco_numero: marco.numero,
      operador_id: avisoOperador,
      destinatarios: avisoDest,
      assunto: dados.assunto,
      corpo_html: dados.corpo_html,
    }, { headers: headers() })
      .then((res) => {
        setMensagem(`${res.data.mensagem}`);
        // marca o marco como avisado na tela sem recarregar tudo
        const agora = new Date().toLocaleString('pt-BR',
          { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
          .replace(',', '');
        setEmpresas(emps => emps.map(e => e.cliente_id !== item.cliente_id ? e : {
          ...e,
          colaboradores: e.colaboradores.map(c => c.convenia_id !== item.convenia_id ? c : {
            ...c,
            marcos: c.marcos.map(m => m.fim !== marco.fim ? m
              : { ...m, aviso_enviado_em: agora, aviso_destinatarios: avisoDest }),
            proximo_marco: c.proximo_marco?.fim === marco.fim
              ? { ...c.proximo_marco, aviso_enviado_em: agora, aviso_destinatarios: avisoDest }
              : c.proximo_marco,
          }),
        }));
        setAviso(null);
      })
      .catch((error) => {
        setMensagem(`${error.response?.data?.mensagem || error.response?.data?.erro || 'Falha no envio'}`);
      })
      .finally(() => setEnviandoAviso(false));
  };

  // ─── cadastro manual do e-mail do gestor (sobrevive ao sync da Convenia) ───
  const [editGestor, setEditGestor] = useState(null);   // {item, email}
  const [salvandoGestor, setSalvandoGestor] = useState(false);

  const salvarGestorEmail = () => {
    const { item, email } = editGestor;
    const limpo = (email || '').trim();
    if (limpo && (!limpo.includes('@') || limpo.includes(' '))) {
      setMensagem('E-mail inválido'); return;
    }
    setSalvandoGestor(true);
    axios.post(`${API}/api/bporh/gestor-email/`, {
      cliente_id: item.cliente_id,
      gestor_convenia_id: item.gestor_convenia_id,
      email: limpo,
    }, { headers: headers() })
      .then((res) => {
        setMensagem(`${res.data.mensagem}`);
        // aplica o e-mail a todos os colaboradores desse gestor na empresa
        setEmpresas(emps => emps.map(e => e.cliente_id !== item.cliente_id ? e : {
          ...e,
          colaboradores: e.colaboradores.map(c =>
            c.gestor_convenia_id === item.gestor_convenia_id
              ? { ...c, gestor_email: limpo || null } : c),
        }));
        setEditGestor(null);
      })
      .catch((error) => {
        setMensagem(`${error.response?.data?.mensagem || error.response?.data?.erro || 'Não foi possível salvar'}`);
      })
      .finally(() => setSalvandoGestor(false));
  };

  // linha do colaborador — usada na visão da empresa e no resumo geral
  // (comEmpresa=true acrescenta a coluna Empresa, clicável para abrir o card)
  const linhaColaborador = (c, comEmpresa = false) => {
    const m1 = c.marcos.find(m => m.numero === 1);
    const m2 = c.marcos.find(m => m.numero === 2);
    const celulaMarco = (m) => {
      if (!m) return <td className="text-muted">—</td>;
      return (
        <td style={{ whiteSpace: 'nowrap' }}>
          <span className="me-2" style={{ fontVariantNumeric: 'tabular-nums' }}>{m.fim}</span>
          <BadgeMarco m={m} />
          {/* o contrato de renovação do 1º limite é cobrado como PENDÊNCIA —
              não impede o aviso ao gestor, que é o que provoca a decisão */}
          {m.exige_contrato && (
            <button
              className={`btn btn-sm ms-2 py-0 px-1 ${m.pendente_contrato
                ? 'btn-outline-secondary' : 'btn-outline-success'}`}
              title={m.pendente_contrato
                ? 'Anexar o contrato de renovação (pendente)'
                : 'Contrato de renovação anexado — clique para ver ou trocar'}
              onClick={() => setAnexoContrato({ item: c })}>
              <i className="fas fa-paperclip"></i>
              {m.pendente_contrato && <span className="ms-1">contrato pendente</span>}
            </button>
          )}
          <button className="btn btn-sm btn-outline-primary ms-2 py-0 px-1"
            title={m.aviso_enviado_em
              ? 'Reenviar o aviso deste marco ao gestor'
              : 'Enviar e-mail ao gestor: manter ou desligar?'}
            onClick={() => abrirAviso(c, m)}>
            <i className="fas fa-envelope"></i>
          </button>
        </td>
      );
    };
    return (
      <tr key={`${c.cliente_id}-${c.convenia_id}`}>
        {comEmpresa && (
          <td style={{ whiteSpace: 'nowrap' }}>
            <button className="btn btn-link btn-sm p-0 fw-semibold text-start"
              style={{ textDecoration: 'none' }}
              title="Abrir a empresa"
              onClick={() => c._emp && abrirEmpresa(c._emp)}>
              {c._empresa}
            </button>
          </td>
        )}
        <td className="fw-bold">
          {nomeCompleto(c)}
          {c.departamento && (
            <div className="small text-muted fw-normal">{c.departamento}</div>
          )}
        </td>
        <td>{c.cargo || '-'}</td>
        <td>
          <VinculoBadge vinculo={c.modelo_contratacao} origem={c.modelo_origem} />
          {vinculoCurto(c.modelo_contratacao) !== 'CLT' && (
            <div className="small" style={{ color: '#8a6d00' }}
              title="Período de experiência é da CLT — confira o cadastro na Convenia">
              <i className="fas fa-triangle-exclamation me-1"></i>sem experiência na CLT
            </div>
          )}
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
          {c.data_admissao || '-'}
          {c.dias_de_casa != null && (
            <div className="small text-muted">{c.dias_de_casa} dias de casa</div>
          )}
        </td>
        <td>
          {/* sem o tipo do período não dá para saber se é 30+60, 45+45 ou
              período único: o campo é obrigatório e só o DP resolve na Convenia */}
          {c.tipo ? c.tipo : (
            <span className="badge"
              style={{ backgroundColor: '#fdeaec', color: '#a11d2b',
                       border: '1.5px solid #dc3545' }}
              title="O tipo do período de experiência não está preenchido na Convenia — cadastre lá (Colaborador → Contrato → Período de experiência)">
              <i className="fas fa-triangle-exclamation me-1"></i>
              cadastrar na Convenia
            </span>
          )}
          {c.total_dias ? (
            <div className="small text-muted">{c.total_dias} dias no total</div>
          ) : null}
        </td>
        {celulaMarco(m1)}
        {celulaMarco(m2)}
        <td>
          {c.gestor || <span className="text-muted">—</span>}
          {c.gestor && c.gestor_convenia_id && (
            <button className="btn btn-sm btn-outline-secondary ms-2 py-0 px-1"
              title={c.gestor_email
                ? `Editar o e-mail do gestor (atual: ${c.gestor_email})`
                : 'Cadastrar o e-mail do gestor'}
              onClick={() => setEditGestor({ item: c, email: c.gestor_email || '' })}>
              <i className={`fas ${c.gestor_email ? 'fa-pen' : 'fa-at'}`}></i>
            </button>
          )}
          {c.gestor_email ? (
            <div className="small text-muted">{c.gestor_email}</div>
          ) : c.gestor && (
            <div className="small" style={{ color: '#8a6d00' }}>
              <i className="fas fa-triangle-exclamation me-1"></i>sem e-mail — cadastre aqui
            </div>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="bporh-admissoes-page">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        {/* Cabeçalho (mesmo padrão das irmãs) */}
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-user-clock header-icon me-2"></i>
            <h2 className="titulo-pagina m-0">
              BPO RH — Prorrogação de Colaborador
              {empresaAtual && <span className="text-muted"> · {empresaAtual.cliente}</span>}
            </h2>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {empresaAtual && (
              <button className="btn btn-outline-secondary btn-sm" onClick={voltarEmpresas}>
                <i className="fas fa-arrow-left me-2"></i>Empresas
              </button>
            )}
            <div className="d-flex align-items-center gap-1"
              title="Só admissões deste mês em diante entram na tela — limpe para ver todas">
              <span className="small text-muted" style={{ whiteSpace: 'nowrap' }}>Admitidos de</span>
              <input type="month" className="form-control form-control-sm" style={{ width: 'auto' }}
                value={admDe} onChange={(e) => setAdmDe(e.target.value)} />
            </div>
            <select className="form-select form-select-sm" style={{ width: 'auto' }}
              value={dias} onChange={(e) => setDias(Number(e.target.value))}>
              {JANELAS.map(j => <option key={j.dias} value={j.dias}>{j.rotulo}</option>)}
            </select>
            <button className="btn btn-outline-primary btn-sm" onClick={carregar} disabled={carregando}>
              <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'} me-2`}></i>Atualizar
            </button>
          </div>
        </div>

        <div className="alert alert-light border small mb-3">
          <i className="fas fa-info-circle me-2 text-primary"></i>
          Contratos de experiência dos colaboradores novos (espelho Convenia). Em cada
          marco — fim do 1º e do 2º período — envie o e-mail ao <b>gestor da área</b>{' '}
          perguntando se ele mantém o colaborador ou encaminha o desligamento.
          O envio fica registrado no marco.
        </div>

        {carregando ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary"></div>
            <p className="text-muted mt-2">Carregando contratos de experiência...</p>
          </div>
        ) : !empresaAtual ? (
          <>
            {/* painel-resumo */}
            <div className="adm-resumo mb-4">
              <div className="adm-kpi">
                <span className="v">{painel.total}</span>
                <span className="k">Em experiência</span>
              </div>
              <div className="adm-kpi pend">
                <span className="v">{painel.a_vencer}</span>
                <span className="k">A vencer (15d)</span>
              </div>
              <div className="adm-kpi pend">
                <span className="v">{painel.vencidos}</span>
                <span className="k">Vencidos sem aviso</span>
              </div>
              <div className="adm-kpi">
                <span className="v">{painel.sem_gestor}</span>
                <span className="k">Sem gestor na Convenia</span>
              </div>
              <div className={`adm-kpi ${painel.sem_tipo ? 'pend' : ''}`}>
                <span className="v">{painel.sem_tipo}</span>
                <span className="k">Sem tipo de experiência</span>
              </div>
            </div>

            {painel.sem_tipo > 0 && (
              <div className="alert d-flex align-items-start gap-2 mb-4"
                style={{ backgroundColor: '#fdeaec', color: '#a11d2b',
                         border: '1px solid #f5c2c7' }}>
                <i className="fas fa-triangle-exclamation mt-1"></i>
                <div>
                  <b>{painel.sem_tipo} colaborador(es) sem o tipo de período de
                  experiência na Convenia.</b>
                  <div className="small mt-1">
                    Sem esse campo não dá para saber se o contrato é 30+60, 45+45 ou
                    período único — as datas dos marcos ficam sem base. O cadastro é
                    obrigatório e só é feito na Convenia, em{' '}
                    <b>Colaborador → Contrato → Período de experiência</b>. Use o filtro{' '}
                    <b>“Sem tipo de experiência”</b> no resumo geral para listar quem falta.
                  </div>
                </div>
              </div>
            )}

            {empresas.length === 0 ? (
              <div className="alert alert-info">
                <i className="fas fa-info-circle me-2"></i>
                Nenhum contrato de experiência na janela selecionada.
              </div>
            ) : (
              <div className="adm-grid">
                {empresas.map((emp, i) => {
                  const pendencias = (emp.a_vencer || 0) + (emp.vencidos || 0);
                  const pct = emp.total > 0
                    ? Math.round(((emp.total - pendencias) / emp.total) * 100) : 0;
                  return (
                    <div key={emp.cliente_id}
                      className={`adm-card ${pendencias > 0 ? 'tem-pendencia' : 'tudo-ok'}`}
                      style={{ '--i': i }}
                      onClick={() => abrirEmpresa(emp)}
                      role="button" tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') abrirEmpresa(emp); }}>
                      <div className="adm-card-top">
                        <h3 className="adm-empresa">{emp.cliente}</h3>
                        <span className="adm-total" title="Colaboradores em experiência">
                          {emp.total}
                        </span>
                      </div>
                      <div className="adm-operador">
                        <i className="fas fa-user-tie"></i>
                        {emp.operador || 'Sem operador'}
                      </div>
                      <div className="adm-progresso" aria-hidden="true">
                        <span style={{ width: `${pct}%` }} />
                      </div>
                      <div className="adm-legenda">
                        {pendencias > 0 ? (
                          <div>
                            {emp.vencidos > 0 && (
                              <span className="pend">
                                <i className="fas fa-triangle-exclamation me-1"></i>
                                {emp.vencidos} vencido{emp.vencidos > 1 ? 's' : ''}
                              </span>
                            )}
                            {emp.vencidos > 0 && emp.a_vencer > 0 && (
                              <span className="text-muted mx-1">·</span>
                            )}
                            {emp.a_vencer > 0 && (
                              <span className="pend">
                                <i className="fas fa-hourglass-half me-1"></i>
                                {emp.a_vencer} a vencer
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="ok">
                            <i className="fas fa-circle-check me-1"></i>
                            Nenhum marco pendente
                          </span>
                        )}
                      </div>
                      <div className="adm-card-acao">
                        Ver colaboradores <i className="fas fa-arrow-right"></i>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* resumo geral: todos os colaboradores de todas as empresas,
                sem precisar entrar em cada card */}
            {empresas.length > 0 && (
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                  <h5 className="fw-bold m-0" style={{ color: '#00205b' }}>
                    <i className="fas fa-list me-2"></i>
                    Resumo geral — todos em experiência
                    <span className="badge bg-secondary ms-2">{geraisFiltrados.length}</span>
                  </h5>
                </div>
                <div className="row g-2 mb-2">
                  <div className="col-md-5">
                    <input type="text" className="form-control form-control-sm"
                      placeholder="Nome, empresa, cargo, departamento, gestor..."
                      value={busca} onChange={(e) => setBusca(e.target.value)} />
                  </div>
                  <div className="col-md-4">
                    <select className="form-select form-select-sm" value={filtroSituacao}
                      onChange={(e) => setFiltroSituacao(e.target.value)}>
                      <option value="">Todas as situações</option>
                      <option value="a_vencer">A vencer, pendente (15 dias)</option>
                      <option value="vencidos">Vencidos, pendentes</option>
                      <option value="sem_tipo">Sem tipo de experiência (cadastrar na Convenia)</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <select className="form-select form-select-sm" value={filtroVinculo}
                      onChange={(e) => setFiltroVinculo(e.target.value)}
                      title="Período de experiência é da CLT — os demais vínculos aqui indicam cadastro a conferir">
                      <option value="CLT">Só CLT</option>
                      <option value="">Todos os vínculos</option>
                      <option value="outros">Só não-CLT (conferir cadastro)</option>
                    </select>
                  </div>
                </div>
                {filtroVinculo === 'CLT' && naoCLT > 0 && (
                  <div className="alert alert-light border small py-2">
                    <i className="fas fa-circle-info me-2 text-primary"></i>
                    {naoCLT} colaborador(es) com período de experiência cadastrado
                    <b> sem ser CLT</b> ficaram fora desta lista — só a CLT tem experiência.
                    Veja em <b>Só não-CLT</b> para conferir o cadastro na Convenia.
                  </div>
                )}
                <div className="table-responsive">
                  <table className="table table-striped table-hover align-middle">
                    <thead>
                      <tr>
                        <th><i className="fas fa-building me-2"></i>Empresa</th>
                        <th><i className="fas fa-user me-2"></i>Colaborador</th>
                        <th><i className="fas fa-briefcase me-2"></i>Cargo</th>
                        <th><i className="fas fa-file-signature me-2"></i>Vínculo</th>
                        <th><i className="fas fa-calendar-plus me-2"></i>Admissão</th>
                        <th><i className="fas fa-file-signature me-2"></i>Experiência</th>
                        <th><i className="fas fa-flag-checkered me-2"></i>1º período</th>
                        <th><i className="fas fa-flag-checkered me-2"></i>2º período</th>
                        <th><i className="fas fa-user-tie me-2"></i>Gestor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {geraisFiltrados.map((c) => linhaColaborador(c, true))}
                      {geraisFiltrados.length === 0 && (
                        <tr>
                          <td colSpan={9} className="text-center text-muted py-4">
                            Nenhum colaborador com esse filtro.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* filtros da visão de colaboradores */}
            <div className="row g-2 mb-3">
              <div className="col-md-5">
                <input type="text" className="form-control form-control-sm"
                  placeholder="Nome, cargo, departamento, gestor..."
                  value={busca} onChange={(e) => setBusca(e.target.value)} />
              </div>
              <div className="col-md-4">
                <select className="form-select form-select-sm" value={filtroSituacao}
                  onChange={(e) => setFiltroSituacao(e.target.value)}>
                  <option value="">Todas as situações</option>
                  <option value="a_vencer">A vencer sem aviso (15 dias)</option>
                  <option value="vencidos">Vencidos sem aviso</option>
                </select>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-striped table-hover align-middle">
                <thead>
                  <tr>
                    <th><i className="fas fa-user me-2"></i>Colaborador</th>
                    <th><i className="fas fa-briefcase me-2"></i>Cargo</th>
                    <th><i className="fas fa-file-signature me-2"></i>Vínculo</th>
                    <th><i className="fas fa-calendar-plus me-2"></i>Admissão</th>
                    <th><i className="fas fa-file-signature me-2"></i>Experiência</th>
                    <th><i className="fas fa-flag-checkered me-2"></i>1º período</th>
                    <th><i className="fas fa-flag-checkered me-2"></i>2º período</th>
                    <th><i className="fas fa-user-tie me-2"></i>Gestor</th>
                  </tr>
                </thead>
                <tbody>
                  {colaboradoresFiltrados.map((c) => linhaColaborador(c))}
                  {colaboradoresFiltrados.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center text-muted py-4">
                        Nenhum colaborador com esse filtro.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ─── MODAL: E-MAIL DO GESTOR ─── */}
        {editGestor && (
          <div className="modal" tabIndex="-1"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10001 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className="fas fa-at me-2"></i>
                      E-mail do gestor
                    </h5>
                    <small style={{ color: '#6c757d' }}>
                      {editGestor.item.gestor} · {editGestor.item.cliente}
                    </small>
                  </div>
                  <button type="button" className="btn-close"
                    onClick={() => setEditGestor(null)} disabled={salvandoGestor}></button>
                </div>
                <div className="modal-body">
                  <label className="form-label small fw-bold mb-1 text-muted">
                    E-mail para os avisos de prorrogação
                  </label>
                  <input type="email" className="form-control" autoFocus
                    value={editGestor.email}
                    onChange={(e) => setEditGestor(g => ({ ...g, email: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') salvarGestorEmail(); }}
                    placeholder="gestor@empresa.com.br" />
                  <small className="text-muted">
                    Vale para todos os colaboradores deste gestor e prevalece sobre o
                    espelho da Convenia (sobrevive à sincronização). Deixe vazio e salve
                    para remover o cadastro.
                  </small>
                </div>
                <div className="modal-footer bg-light">
                  <button type="button" className="btn btn-secondary"
                    onClick={() => setEditGestor(null)} disabled={salvandoGestor}>
                    <i className="fas fa-times me-2"></i>Cancelar
                  </button>
                  <button type="button" className="btn btn-success"
                    onClick={salvarGestorEmail} disabled={salvandoGestor}>
                    <i className={`fas ${salvandoGestor ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-2`}></i>
                    {salvandoGestor ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── MODAL: AVISO AO GESTOR ─── */}
        {aviso && (
          <div className="modal" tabIndex="-1"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className="fas fa-envelope me-2"></i>
                      Aviso de prorrogação — {aviso.marco.numero}º período
                    </h5>
                    <small style={{ color: '#6c757d' }}>
                      {nomeCompleto(aviso.item)} · {aviso.item.cliente} · vence em {aviso.marco.fim}
                      {aviso.marco.aviso_enviado_em && ` · já avisado em ${aviso.marco.aviso_enviado_em}`}
                    </small>
                  </div>
                  <button type="button" className="btn-close"
                    onClick={() => setAviso(null)} disabled={enviandoAviso}></button>
                </div>

                <div className="modal-body">
                  {aviso.carregando ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status"></div>
                      <p className="text-muted mt-2 mb-0">Montando a prévia...</p>
                    </div>
                  ) : (
                    <>
                      {/* prorrogar exige o termo assinado — o aviso do 2º marco
                          só sai depois que o PDF é anexado e conferido */}
                      {aviso.marco?.exige_contrato && (
                        <div className={`alert small ${aviso.marco.pendente_contrato
                          ? 'alert-warning' : 'alert-light border'}`}>
                          <div className="fw-bold mb-1">
                            <i className={`fas ${aviso.marco.pendente_contrato
                              ? 'fa-triangle-exclamation' : 'fa-file-signature'} me-2`}></i>
                            Contrato de renovação
                          </div>
                          {aviso.marco.pendente_contrato
                            ? 'Ainda não foi anexado o contrato de renovação deste colaborador. '
                              + 'O aviso pode ser enviado assim mesmo, mas a pendência continua '
                              + 'marcada até o PDF entrar.'
                            : 'Contrato de renovação anexado e assinado.'}
                          <ContratoAnexo
                            tipo="prorrogacao"
                            clienteId={aviso.item.cliente_id}
                            conveniaId={aviso.item.convenia_id}
                            colaborador={aviso.dados?.colaborador || aviso.item.nome}
                            vinculo={aviso.item.tipo}
                            aviso={false}
                            onMudou={() => carregar()}
                          />
                        </div>
                      )}
                      <div className="row g-2 mb-3">
                        <div className="col-md-7">
                          <label className="form-label small fw-bold mb-1 text-muted">
                            Destinatários (edite à vontade — separe por ; )
                          </label>
                          <input type="text" className="form-control form-control-sm"
                            value={avisoDest}
                            onChange={(e) => setAvisoDest(e.target.value)}
                            placeholder="gestor@empresa.com.br" />
                          {!aviso.dados.gestor_email && aviso.dados.gestor && (
                            <small className="text-muted">
                              Gestor "{aviso.dados.gestor}" sem e-mail no espelho —
                              acrescente manualmente.
                            </small>
                          )}
                          {!aviso.dados.gestor && (
                            <small className="text-muted">
                              Colaborador sem gestor cadastrado na Convenia — informe o
                              destinatário manualmente.
                            </small>
                          )}
                        </div>
                        <div className="col-md-5">
                          <label className="form-label small fw-bold mb-1 text-muted">
                            Enviar com a conta de
                          </label>
                          {/* trocar quem assina refaz a prévia — assinatura do
                              e-mail acompanha quem realmente envia */}
                          <select className="form-select form-select-sm" value={avisoOperador}
                            disabled={atualizandoPrevia}
                            onChange={(e) => trocarOperadorAviso(e.target.value)}>
                            <option value="">Selecione o operador...</option>
                            {(aviso.dados.operadores || []).map(o => (
                              <option key={o.id} value={o.id} disabled={!o.pronto}>
                                {o.nome}{!o.pronto ? ' (sem senha de app)' : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="small text-muted mb-1">
                        <b>Assunto:</b> {aviso.dados.assunto}
                      </div>
                      <div className="border rounded" style={{ height: '420px', overflow: 'hidden' }}>
                        <iframe title="Prévia do aviso de prorrogação"
                          srcDoc={aviso.dados.corpo_previa}
                          style={{ width: '100%', height: '100%', border: 0 }} />
                      </div>
                    </>
                  )}
                </div>

                <div className="modal-footer bg-light">
                  <button type="button" className="btn btn-secondary"
                    onClick={() => setAviso(null)} disabled={enviandoAviso}>
                    <i className="fas fa-times me-2"></i>
                    Cancelar
                  </button>
                  {!aviso.carregando && (
                    <button type="button" className="btn btn-success"
                      onClick={enviarAviso} disabled={enviandoAviso || atualizandoPrevia}>
                      <i className={`fas ${(enviandoAviso || atualizandoPrevia) ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-2`}></i>
                      {enviandoAviso ? 'Enviando...'
                        : atualizandoPrevia ? 'Atualizando prévia...'
                        : aviso.marco.aviso_enviado_em ? 'Confirmar reenvio' : 'Confirmar envio'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* ─── MODAL: contrato de renovação (1º limite) ─── */}
      {anexoContrato && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10001 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '560px' }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-file-signature me-2"></i>Contrato de renovação
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {nomeCompleto(anexoContrato.item)}
                    {anexoContrato.item.cliente && ` · ${anexoContrato.item.cliente}`}
                  </small>
                </div>
                <button type="button" className="btn-close"
                  onClick={() => setAnexoContrato(null)}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-light border small">
                  <i className="fas fa-circle-info me-2 text-primary"></i>
                  Termo assinado ao fim do 1º período de experiência, quando o cliente
                  decide manter o colaborador. Enquanto não for anexado, o processo fica
                  com a pendência marcada — o aviso ao gestor continua liberado.
                </div>
                <ContratoAnexo
                  tipo="prorrogacao"
                  clienteId={anexoContrato.item.cliente_id}
                  conveniaId={anexoContrato.item.convenia_id}
                  colaborador={nomeCompleto(anexoContrato.item)}
                  vinculo={anexoContrato.item.modelo_contratacao}
                  onMudou={() => carregar()}
                />
              </div>
              <div className="modal-footer bg-light">
                <button className="btn btn-secondary" onClick={() => setAnexoContrato(null)}>
                  <i className="fas fa-times me-2"></i>Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BpoRhProrrogacao;
